import type { IntlShape } from 'react-intl';

const STORE_CREDIT_VARIABLE_CODE = /^SCV(\d+)(?:-\d+)?-[A-F0-9]{6}$/i;
const STORE_CREDIT_LEGACY_CODE = /^SC(\d+)-[A-F0-9]{6}$/i;
const PFOTENSHOP_DISCOUNT_CODE = /^PS50(\d+)(?:-\d+)?-[A-F0-9]{6}$/i;

export const storeCreditAmountFromCode = (title?: string | null): number | null => {
  if (!title) return null;

  const variable = title.match(STORE_CREDIT_VARIABLE_CODE);
  if (variable) return parseInt(variable[1], 10) / 100;

  const legacy = title.match(STORE_CREDIT_LEGACY_CODE);
  if (legacy) return parseInt(legacy[1], 10);

  return null;
};

// Variant a redemption funds (code shape `SCV{cents}-{variantId}-{HASH}`);
// null for legacy/unscoped codes. Mirrors backend extract_variant_from_code.
const STORE_CREDIT_VARIANT_CODE = /^SCV\d+-(\d+)-[A-F0-9]{6}$/i;

export const storeCreditVariantFromCode = (title?: string | null): string | null => {
  if (!title) return null;
  const match = title.match(STORE_CREDIT_VARIANT_CODE);
  return match ? match[1] : null;
};

// True for auto-generated Pfotenpunkte redemption codes. They aren't
// user-managed promo codes, so they're hidden from the discount list — the
// reduction shows on the funded line instead.
export const isStoreCreditDiscountCode = (title?: string | null): boolean =>
  storeCreditAmountFromCode(title) != null;

// Variant a Sommer Aktion campaign code is scoped to (`PS50{cents}-{variantId}-{HASH}`);
// null for unscoped codes. Mirrors the backend's apply_pfotenshop_item_discount.
const PFOTENSHOP_VARIANT_CODE = /^PS50\d+-(\d+)-[A-F0-9]{6}$/i;

export const pfotenshopVariantFromCode = (title?: string | null): string | null => {
  if (!title) return null;
  const match = title.match(PFOTENSHOP_VARIANT_CODE);
  return match ? match[1] : null;
};

// The shop line item (variantShopifyId) a discount is tied to, when the code
// carries one — either a Pfotenpunkte redemption (SCV…) or a Sommer Aktion
// campaign code (PS50…). Returns null for normal subscription-level promo
// codes that apply to the order as a whole.
export const discountVariantFromCode = (title?: string | null): string | null =>
  storeCreditVariantFromCode(title) ?? pfotenshopVariantFromCode(title);

// True when a discount belongs to a specific shop line item rather than the
// subscription itself. These render on the funded item card, not in the
// subscription-level discount list.
export const isItemScopedDiscount = (title?: string | null): boolean =>
  discountVariantFromCode(title) != null;

/**
 * Returns the display label for a discount title: Pfotenpunkte codes become
 * "Pfotenpunkte (−20,00 €)", Pfotenshop campaign codes become
 * "Sommer Aktion (−9,99 €)", anything else passes through unchanged.
 */
export const formatDiscountTitle = (
  intl: IntlShape,
  title?: string | null,
  currencySymbol = '€',
): string | null => {
  // Takes `intl` rather than a bare locale because it needs both: the amount has to
  // be formatted for the active locale, and the campaign names are copy. Every caller
  // is a component, so the dependency stays explicit instead of going through the
  // global in shared/i18n/intlInstance.
  const formatAmount = (value: number) =>
    intl.formatNumber(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // The surrounding "(−… €)" / "(−CHF …)" shape is left in code: only the name is
  // copy, and the currency placement is the existing display, not a translation.
  const withAmount = (name: string, value: number) =>
    currencySymbol === 'CHF'
      ? `${name} (−CHF ${formatAmount(value)})`
      : `${name} (−${formatAmount(value)} €)`;

  // Pfotenshop subscriber campaign codes
  const ps50Match = title?.match(PFOTENSHOP_DISCOUNT_CODE);
  if (ps50Match) {
    const amount = parseInt(ps50Match[1], 10) / 100;
    return withAmount(intl.formatMessage({ id: 'DISCOUNT.SUMMER_CAMPAIGN' }), amount);
  }

  // Store credit / Pfotenpunkte codes
  const amount = storeCreditAmountFromCode(title);
  if (amount == null) return title ?? null;

  return withAmount(intl.formatMessage({ id: 'DISCOUNT.PFOTENPUNKTE' }), amount);
};
